/**
 * RevenueDashboard - Admin dashboard for payment analytics and revenue tracking
 * Features: Revenue charts, transaction history, payment stats, refund management
 */
import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead,
  TableHeader, TableRow
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  DollarSign, TrendingUp, TrendingDown, CreditCard, Users, Clock,
  CheckCircle2, XCircle, AlertCircle, RefreshCw, Download, Search,
  ArrowUpRight, ArrowDownRight, Loader2, Calendar, Filter, Heart, Receipt
} from 'lucide-react';
import { toast } from 'sonner';
import { paymentAPI, financeAPI } from '@/lib/api';
import { getToken } from '@/lib/authToken';
import { formatCurrency as fmtCurrency } from '@/lib/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Color palette
const COLORS = {
  primary: '#3B82F6',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  purple: '#8B5CF6',
  pink: '#EC4899',
};

const PIE_COLORS = ['#10B981', '#F59E0B', '#EF4444', '#6B7280'];

// Status badge styles
const statusStyles = {
  paid: { bg: 'bg-emerald-100', text: 'text-emerald-800', icon: CheckCircle2 },
  initiated: { bg: 'bg-amber-100', text: 'text-amber-800', icon: Clock },
  pending: { bg: 'bg-amber-100', text: 'text-amber-800', icon: Clock },
  failed: { bg: 'bg-rose-100', text: 'text-rose-800', icon: XCircle },
  expired: { bg: 'bg-slate-100', text: 'text-slate-800', icon: AlertCircle },
  refunded: { bg: 'bg-purple-100', text: 'text-purple-800', icon: RefreshCw },
};

const RevenueDashboard = ({ eventId }) => {
  const [timeRange, setTimeRange] = useState('7d');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currency, setCurrency] = useState('USD');
  const token = getToken();

  // Fetch payment stats (Stripe transactions)
  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useQuery({
    queryKey: ['payment-stats', eventId],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/api/admin/events/${eventId}/payments/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch stats');
      return response.json();
    },
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  });

  // Fetch Stripe transactions
  const { data: transactions = [], isLoading: transactionsLoading, refetch: refetchTransactions } = useQuery({
    queryKey: ['payment-transactions', eventId],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/api/admin/events/${eventId}/payments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch transactions');
      return response.json();
    },
    refetchInterval: 30000,
  });

  // Event Finance summary (income / expenses / donations entered in Events → Finance)
  const { data: finance, refetch: refetchFinance } = useQuery({
    queryKey: ['event-finance-summary', eventId, currency],
    queryFn: () => financeAPI.getSummary(eventId, { params: { currency, approved_only: true } })
      .then(r => r.data),
    enabled: !!eventId,
    refetchInterval: 30000,
  });

  // Process data for charts
  const chartData = React.useMemo(() => {
    if (!transactions.length) return { daily: [], byTier: [], byStatus: [] };

    // Group by date for daily revenue
    const dailyMap = {};
    const tierMap = {};
    const statusMap = { paid: 0, pending: 0, failed: 0, other: 0 };

    transactions.forEach(tx => {
      // Daily revenue
      const date = new Date(tx.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      if (!dailyMap[date]) {
        dailyMap[date] = { date, revenue: 0, count: 0 };
      }
      if (tx.payment_status === 'paid') {
        dailyMap[date].revenue += tx.amount || 0;
      }
      dailyMap[date].count += 1;

      // By tier
      const tierName = tx.metadata?.tier_name || 'Unknown';
      if (!tierMap[tierName]) {
        tierMap[tierName] = { name: tierName, value: 0, count: 0 };
      }
      if (tx.payment_status === 'paid') {
        tierMap[tierName].value += tx.amount || 0;
        tierMap[tierName].count += 1;
      }

      // By status
      if (tx.payment_status === 'paid') statusMap.paid += 1;
      else if (['initiated', 'pending'].includes(tx.payment_status)) statusMap.pending += 1;
      else if (tx.payment_status === 'failed') statusMap.failed += 1;
      else statusMap.other += 1;
    });

    return {
      daily: Object.values(dailyMap).slice(-14), // Last 14 days
      byTier: Object.values(tierMap),
      byStatus: [
        { name: 'Paid', value: statusMap.paid, color: COLORS.success },
        { name: 'Pending', value: statusMap.pending, color: COLORS.warning },
        { name: 'Failed', value: statusMap.failed, color: COLORS.danger },
      ].filter(s => s.value > 0)
    };
  }, [transactions]);

  // Filter transactions
  const filteredTransactions = React.useMemo(() => {
    return transactions.filter(tx => {
      const matchesSearch = !searchTerm ||
        tx.delegate_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tx.delegate_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tx.session_id?.includes(searchTerm);
      const matchesStatus = statusFilter === 'all' || tx.payment_status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [transactions, searchTerm, statusFilter]);

  // Format currency — canonical formatter (handles ZWG / ZIG)
  const formatCurrency = (amount, cur = currency) => fmtCurrency(amount, cur || 'USD');

  // Format date
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Export to CSV
  const exportCSV = () => {
    const headers = ['Date', 'Email', 'Name', 'Amount', 'Status', 'Session ID'];
    const rows = filteredTransactions.map(tx => [
      new Date(tx.created_at).toISOString(),
      tx.delegate_email,
      tx.delegate_name,
      tx.amount,
      tx.payment_status,
      tx.session_id,
    ]);
    
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payments_${eventId}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported payments to CSV');
  };

  const isLoading = statsLoading || transactionsLoading;

  // Available currencies from event finance data
  const availableCurrencies = React.useMemo(() => {
    const set = new Set(['USD']);
    Object.keys(finance?.income_totals_by_currency || {}).forEach(c => set.add(c));
    Object.keys(finance?.expense_totals_by_currency || {}).forEach(c => set.add(c));
    return Array.from(set);
  }, [finance]);

  // Combined revenue: Stripe payments (assume USD) + event finance income
  const stripeRevenue = currency === 'USD' ? (stats?.total_revenue || 0) : 0;
  const eventIncome = finance?.income_totals_by_currency?.[currency] || 0;
  const eventExpense = finance?.expense_totals_by_currency?.[currency] || 0;
  const eventDonations = finance?.donations_totals_by_currency?.[currency] || 0;
  const totalRevenue = stripeRevenue + eventIncome;
  const netRevenue = totalRevenue - eventExpense;

  const downloadEventCsv = (kind) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const url = `${base}/api/admin/events/${eventId}/finance/export.csv?kind=${kind}&approved_only=true`;
    // Use fetch with auth then blob-download (backend routes are auth-protected)
    fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => {
        if (!r.ok) throw new Error(`Export failed (${r.status})`);
        return r.blob();
      })
      .then(blob => {
        const dl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = dl;
        a.download = `finance-${eventId}-${kind}.csv`;
        a.click();
        URL.revokeObjectURL(dl);
        toast.success(`Exported ${kind} to CSV`);
      })
      .catch(err => toast.error(err.message || 'Export failed'));
  };

  return (
    <div className="space-y-6" data-testid="revenue-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <DollarSign className="h-6 w-6 text-emerald-600" />
            Revenue Dashboard
          </h2>
          <p className="text-slate-500 text-sm">
            Live financial summary — pulls approved income &amp; expenses from Event Finance + Stripe.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger className="w-24" data-testid="revenue-currency">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableCurrencies.map(c => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => { refetchStats(); refetchTransactions(); refetchFinance(); }} data-testid="revenue-refresh">
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportCSV} data-testid="revenue-export-stripe">
            <Download className="h-4 w-4 mr-1" />
            Payments CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => downloadEventCsv('all')} data-testid="revenue-export-all">
            <Download className="h-4 w-4 mr-1" />
            Full Ledger CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => downloadEventCsv('donations')} data-testid="revenue-export-donations">
            <Heart className="h-4 w-4 mr-1" />
            Donations CSV
          </Button>
        </div>
      </div>

      {/* Live Financial Summary — Revenue / Expenses / Net / Donations */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-emerald-500" data-testid="revenue-summary-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Revenue ({currency})</p>
                <p className="text-3xl font-bold text-emerald-600" data-testid="revenue-total">
                  {formatCurrency(totalRevenue)}
                </p>
              </div>
              <div className="p-3 bg-emerald-100 rounded-full">
                <TrendingUp className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
            <div className="mt-2 text-xs text-slate-500">
              Stripe {formatCurrency(stripeRevenue)} · Events {formatCurrency(eventIncome)}
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-rose-500" data-testid="expenses-summary-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Expenses ({currency})</p>
                <p className="text-3xl font-bold text-rose-600" data-testid="revenue-expenses-total">
                  {formatCurrency(eventExpense)}
                </p>
              </div>
              <div className="p-3 bg-rose-100 rounded-full">
                <Receipt className="h-6 w-6 text-rose-600" />
              </div>
            </div>
            <div className="mt-2 text-xs text-slate-500">approved expenditure only</div>
          </CardContent>
        </Card>

        <Card className={`border-l-4 ${netRevenue >= 0 ? 'border-l-emerald-500' : 'border-l-rose-500'}`} data-testid="net-revenue-summary-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Net Revenue ({currency})</p>
                <p className={`text-3xl font-bold ${netRevenue >= 0 ? 'text-emerald-600' : 'text-rose-600'}`} data-testid="revenue-net-total">
                  {formatCurrency(netRevenue)}
                </p>
              </div>
              <div className={`p-3 rounded-full ${netRevenue >= 0 ? 'bg-emerald-100' : 'bg-rose-100'}`}>
                {netRevenue >= 0
                  ? <TrendingUp className="h-6 w-6 text-emerald-600" />
                  : <TrendingDown className="h-6 w-6 text-rose-600" />}
              </div>
            </div>
            <div className="mt-2 text-xs text-slate-500">revenue − expenses</div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-pink-500" data-testid="donations-summary-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Donations ({currency})</p>
                <p className="text-3xl font-bold text-pink-600" data-testid="revenue-donations-total">
                  {formatCurrency(eventDonations)}
                </p>
              </div>
              <div className="p-3 bg-pink-100 rounded-full">
                <Heart className="h-6 w-6 text-pink-600" />
              </div>
            </div>
            <div className="mt-2 text-xs text-slate-500">approved donations only</div>
          </CardContent>
        </Card>
      </div>

      {/* Multi-currency chips */}
      {availableCurrencies.length > 1 && (
        <Card data-testid="multi-currency-strip">
          <CardContent className="p-3 flex items-center gap-3 flex-wrap">
            <span className="text-xs uppercase tracking-wide text-slate-500 font-semibold mr-2">All currencies</span>
            {availableCurrencies.map(c => {
              const inc = finance?.income_totals_by_currency?.[c] || 0;
              const exp = finance?.expense_totals_by_currency?.[c] || 0;
              if (inc === 0 && exp === 0) return null;
              return (
                <button
                  key={c}
                  onClick={() => setCurrency(c)}
                  className={`text-xs px-3 py-1.5 rounded-full border ${c === currency ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-semibold' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
                  data-testid={`multi-currency-chip-${c}`}
                >
                  <span className="font-semibold">{c}</span> · {formatCurrency(inc - exp, c)}
                </button>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Donations breakdown */}
      {(finance?.donations_breakdown || []).filter(d => d.currency === currency).length > 0 && (
        <Card data-testid="donations-breakdown-panel">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Heart className="h-4 w-4 text-pink-600" />
              Donations Breakdown ({currency})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {finance.donations_breakdown
                .filter(d => d.currency === currency)
                .map((d, i) => (
                  <div key={i} className="p-3 flex items-center gap-3" data-testid={`donation-row-${d.category}`}>
                    <div className="flex-1">
                      <p className="font-medium capitalize">{(d.category || 'other').replace(/_/g, ' ')}</p>
                      <p className="text-xs text-slate-500">{d.count} record{d.count === 1 ? '' : 's'}</p>
                    </div>
                    <span className="font-semibold text-pink-700">{formatCurrency(d.total, d.currency)}</span>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Over Time */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg">Revenue Over Time</CardTitle>
            <CardDescription>Daily revenue for the last 14 days</CardDescription>
          </CardHeader>
          <CardContent>
            {chartData.daily.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={chartData.daily}>
                  <defs>
                    <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS.success} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={COLORS.success} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#9CA3AF" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#9CA3AF" tickFormatter={(v) => `$${v}`} />
                  <Tooltip
                    formatter={(value) => [formatCurrency(value), 'Revenue']}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke={COLORS.success}
                    strokeWidth={2}
                    fill="url(#revenueGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-slate-400">
                <p>No revenue data available</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payment Status Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Payment Status</CardTitle>
            <CardDescription>Distribution by status</CardDescription>
          </CardHeader>
          <CardContent>
            {chartData.byStatus.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={chartData.byStatus}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {chartData.byStatus.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [value, 'Transactions']} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-slate-400">
                <p>No transaction data</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Revenue by Tier */}
      {chartData.byTier.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Revenue by Ticket Tier</CardTitle>
            <CardDescription>Breakdown of revenue per ticket type</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData.byTier} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis type="number" tickFormatter={(v) => `$${v}`} />
                <YAxis type="category" dataKey="name" width={100} />
                <Tooltip formatter={(value) => [formatCurrency(value), 'Revenue']} />
                <Bar dataKey="value" fill={COLORS.primary} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Transactions Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle className="text-lg">Transaction History</CardTitle>
              <CardDescription>All payment transactions for this event</CardDescription>
            </div>
            <div className="flex gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by email or name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 w-64"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="initiated">Initiated</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {transactionsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : filteredTransactions.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No transactions found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Session ID</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTransactions.slice(0, 50).map((tx) => {
                    const status = statusStyles[tx.payment_status] || statusStyles.pending;
                    const StatusIcon = status.icon;
                    return (
                      <TableRow key={tx.id || tx.session_id} data-testid={`tx-${tx.session_id}`}>
                        <TableCell className="whitespace-nowrap">
                          {formatDate(tx.created_at)}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{tx.delegate_name || 'N/A'}</p>
                            <p className="text-sm text-slate-500">{tx.delegate_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {tx.metadata?.tier_name || 'Unknown'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono font-medium">
                          {formatCurrency(tx.amount, tx.currency)}
                        </TableCell>
                        <TableCell>
                          <Badge className={`${status.bg} ${status.text} gap-1`}>
                            <StatusIcon className="h-3 w-3" />
                            {tx.payment_status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <code className="text-xs bg-slate-100 px-2 py-1 rounded">
                            {tx.session_id?.slice(0, 20)}...
                          </code>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {filteredTransactions.length > 50 && (
                <p className="text-center text-sm text-slate-500 mt-4">
                  Showing 50 of {filteredTransactions.length} transactions
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RevenueDashboard;
